import { useState, type ComponentType } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { Building2, Store, KeyRound, ShieldCheck, Tags, Receipt, Users, Link2, ListChecks, Megaphone, TrendingUp, BadgeDollarSign, SlidersHorizontal, Send, Check, History, LayoutDashboard, LogOut, Menu, MessageSquare, Monitor, Moon, Package, PanelLeftClose, PanelLeftOpen, Plug, Rows2, Rows4, Settings, ShoppingCart, Sun, Truck, FilePlus2, FileCheck2, Scale, Wallet, Landmark, LineChart, Swords, PackageCheck, Boxes, BookCheck, MessagesSquare, LifeBuoy, Target, BadgeCheck, HeartPulse } from 'lucide-react';
import type { Access, ChannelInfo, Density, Gate, ThemePref } from '@berytus/contracts';
import { can, homeOf, permOfPath } from '@berytus/contracts';
import { api } from '../api.ts';
import { useMe } from './AuthGate.tsx';
import { BandLogo } from './Brand.tsx';
import { AccountNotices, TrialEnded } from './Trial.tsx';
import { pickAccount, selectAccount, useSelectedAccountId } from './AccountPicker.tsx';
import { NotificationBell } from './Notifications.tsx';
import { ImpersonationBar } from '../pages/Admin.tsx';
import { useApplyPreferences, useNarrowScreen, usePreferences } from '../prefs.ts';

type Icon = ComponentType<{ size?: number }>;
interface NavItem {
  to?: string;
  label: string;
  icon: Icon;
  end?: boolean;
  soon?: boolean;
  /** Quem vê o item (permissão). Sem isto, vale a área da tela (permOfPath). */
  area?: Gate;
  /** Só para contas de farmácia. */
  pharmacy?: boolean;
  /** Painel Berytus: chave do contador da fila (GET /admin/nav-counts). */
  count?: string;
}

type NavSection = { group?: string; items: NavItem[] };

/** Só os itens que o usuário pode abrir; grupos vazios somem. */
function navFor(nav: NavSection[], access: Access, vertical?: string): NavSection[] {
  return nav
    .map((sec) => ({ ...sec, items: sec.items.filter((i) => (!i.pharmacy || vertical === 'pharmacy') && can(access, i.area !== undefined ? i.area : i.to ? permOfPath(i.to) : 'settings')) }))
    .filter((sec) => sec.items.length > 0);
}

const NAV: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Painel geral', icon: LayoutDashboard, end: true },
      { to: '/catalogo', label: 'Catálogo', icon: Package, end: true },
      { to: '/catalogo/kits', label: 'Kits e preço', icon: Boxes },
      { to: '/importacoes', label: 'Importações', icon: History },
    ],
  },
  {
    group: 'Vendas',
    items: [
      { to: '/vendas', label: 'Vendas', icon: LineChart, end: true },
      { to: '/vendas/resultados', label: 'Resultados', icon: TrendingUp },
    ],
  },
  {
    group: 'Operação',
    items: [
      { to: '/pedidos', label: 'Pedidos', icon: ShoppingCart },
      { to: '/fila', label: 'Fila de envios', icon: Send },
      { label: 'Expedição', icon: Truck, soon: true, area: 'deliveries' },
      { to: '/canal/mercadolivre/perguntas', label: 'Perguntas (ML)', icon: MessageSquare },
      { to: '/canal/mercadolivre/mensagens', label: 'Mensagens (ML)', icon: MessagesSquare },
      { to: '/canal/mercadolivre/reclamacoes', label: 'Reclamações (ML)', icon: LifeBuoy },
    ],
  },
  {
    group: 'Financeiro',
    items: [
      { to: '/financeiro/conciliacao', label: 'Conciliação', icon: Scale },
      { to: '/financeiro/repasses', label: 'Repasses', icon: Landmark },
      { to: '/financeiro/despesas', label: 'Despesas', icon: Wallet },
    ],
  },
  {
    group: 'Configuração',
    items: [
      { to: '/canais', label: 'Integração de canais', icon: Plug },
      { to: '/empresa', label: 'Dados da empresa', icon: Building2 },
      { to: '/configuracoes/farmacia', label: 'Dados da farmácia', icon: FileCheck2, pharmacy: true },
      { to: '/configuracoes/autorizacoes-marca', label: 'Autorizações de marca', icon: BadgeCheck },
      { to: '/configuracoes/lojas', label: 'Lojas e estoques', icon: Store },
      { to: '/configuracoes/metas', label: 'Metas e feriados', icon: Target },
      { to: '/configuracoes/atributos', label: 'Atributos personalizados', icon: Tags },
      { to: '/configuracoes/regras-tributarias', label: 'Regras tributárias', icon: Receipt },
      { to: '/configuracoes/api', label: 'Integração por API', icon: KeyRound },
      { to: '/configuracoes/usuarios', label: 'Usuários e permissões', icon: Users },
      { to: '/configuracoes/gerais', label: 'Configurações gerais', icon: Settings },
    ],
  },
];

/** Menu da aba de um canal: muda junto com a aba. */
const MELI_NAV: NavSection[] = [
  {
    group: 'Mercado Livre',
    items: [
      { to: '/canal/mercadolivre', label: 'Resumo do canal', icon: LayoutDashboard, end: true },
      { to: '/canal/mercadolivre/produtos', label: 'Produtos × anúncios', icon: ListChecks },
      { to: '/canal/mercadolivre/anuncios', label: 'Anúncios', icon: Megaphone },
      { to: '/canal/mercadolivre/precos', label: 'Preço e estoque', icon: BadgeDollarSign },
      { to: '/canal/mercadolivre/envio', label: 'Envio dos anúncios', icon: PackageCheck },
      { to: '/canal/mercadolivre/entrar-catalogo', label: 'Entrar no catálogo', icon: BookCheck },
      { to: '/canal/mercadolivre/sem-catalogo', label: 'Sem catálogo', icon: FilePlus2 },
      { to: '/canal/mercadolivre/publicacoes', label: 'Publicações', icon: Send },
      { to: '/canal/mercadolivre/conciliar', label: 'Conciliar vínculos', icon: Link2 },
      { to: '/canal/mercadolivre/oportunidades', label: 'Oportunidades', icon: TrendingUp },
      { to: '/canal/mercadolivre/concorrentes', label: 'Buy Box e concorrentes', icon: Swords },
    ],
  },
  {
    group: 'Operação',
    items: [
      { to: '/pedidos?channel=mercadolivre', label: 'Pedidos', icon: ShoppingCart },
      { to: '/canal/mercadolivre/entregas', label: 'Entregas', icon: Truck },
      { to: '/canal/mercadolivre/receitas', label: 'Receitas', icon: FileCheck2 },
      { to: '/canal/mercadolivre/perguntas', label: 'Perguntas', icon: MessageSquare },
      { to: '/canal/mercadolivre/mensagens', label: 'Mensagens', icon: MessagesSquare },
      { to: '/canal/mercadolivre/reclamacoes', label: 'Reclamações', icon: LifeBuoy },
    ],
  },
  {
    group: 'Configuração',
    items: [
      { to: '/canal/mercadolivre/configuracoes', label: 'Configurações do canal', icon: SlidersHorizontal },
      { to: '/canais', label: 'Conta e conexão', icon: Plug },
    ],
  },
];

const SHOPEE_NAV: NavSection[] = [
  {
    group: 'Shopee',
    items: [
      { to: '/canal/shopee', label: 'Resumo do canal', icon: LayoutDashboard, end: true },
      { to: '/canal/shopee/saude', label: 'Saúde da conta', icon: HeartPulse },
      { to: '/canal/shopee/anuncios', label: 'Anúncios', icon: Megaphone },
      { to: '/canal/shopee/publicar', label: 'Publicar na Shopee', icon: FilePlus2 },
      { to: '/canal/shopee/publicacoes', label: 'Publicações', icon: Send },
      { to: '/canal/shopee/precos', label: 'Preço e estoque', icon: BadgeDollarSign },
      { to: '/canal/shopee/entrar-catalogo', label: 'Entrar no catálogo', icon: BookCheck },
      { to: '/canal/shopee/buybox', label: 'Buybox', icon: Swords },
    ],
  },
  {
    group: 'Operação',
    items: [
      { to: '/pedidos?channel=shopee', label: 'Pedidos', icon: ShoppingCart },
    ],
  },
  {
    group: 'Configuração',
    items: [
      { to: '/canal/shopee/configuracoes', label: 'Configurações do canal', icon: SlidersHorizontal },
      { to: '/canais', label: 'Conta e conexão', icon: Plug },
    ],
  },
];

const IFOOD_NAV: NavSection[] = [
  {
    group: 'iFood',
    items: [
      { to: '/canal/ifood', label: 'Lojas e pedidos', icon: LayoutDashboard, end: true },
      { to: '/pedidos?channel=ifood', label: 'Pedidos', icon: ShoppingCart },
      { to: '/canal/ifood/precos', label: 'Preço e estoque', icon: BadgeDollarSign },
    ],
  },
  {
    group: 'Configuração',
    items: [
      { to: '/canal/ifood/configuracoes', label: 'Configurações do canal', icon: SlidersHorizontal },
      { to: '/canais', label: 'Conta e conexão', icon: Plug },
    ],
  },
];

/**
 * Painel master da Berytus (só equipe), por área: Visão geral · Clientes · Catálogo · Curadoria · Compliance ·
 * Infraestrutura. `count` é a chave do contador da fila (GET /admin/nav-counts); o grupo mostra a soma, ex.: Curadoria (12).
 */
const ADMIN_NAV: NavSection[] = [
  { group: 'Painel Berytus', items: [{ to: '/admin', label: 'Visão geral', icon: LayoutDashboard, end: true }] },
  {
    group: 'Clientes',
    items: [
      { to: '/admin/clientes', label: 'Contas', icon: Users },
      { to: '/admin/compliance?aba=liberacoes', label: 'Liberações provisórias', icon: BadgeCheck },
      { to: '/admin/compliance?aba=controlados', label: 'Liberações de controlados', icon: ShieldCheck, count: 'controlados' },
    ],
  },
  {
    group: 'Catálogo',
    items: [
      { to: '/admin/catalogo', label: 'Catálogo mestre', icon: Package },
      { to: '/admin/fontes', label: 'Fontes do mestre', icon: Boxes },
      { to: '/admin/acoes-sanitarias?aba=search', label: 'Pesquisar e bloquear', icon: ShieldCheck },
      { to: '/admin/restricoes-marca', label: 'Venda restrita por marca', icon: BadgeCheck },
    ],
  },
  {
    group: 'Curadoria',
    items: [
      { to: '/admin/itens-novos', label: 'Itens novos', icon: Package, count: 'itens_novos' },
      { to: '/admin/candidatos', label: 'Candidatos ao mestre', icon: Package, count: 'candidatos' },
      { to: '/admin/contestacoes', label: 'Contestações', icon: MessagesSquare, count: 'contestacoes' },
      { to: '/admin/divergencias', label: 'Divergências', icon: ShieldCheck, count: 'divergencias' },
      { to: '/admin/sem-bula', label: 'Sem bula', icon: FileCheck2, count: 'sem_bula' },
      { to: '/admin/acoes-sanitarias?aba=actions', label: 'Recall e descontinuação', icon: ShieldCheck, count: 'recall' },
      { to: '/admin/documentos', label: 'Documentos das farmácias', icon: FileCheck2, count: 'documentos' },
    ],
  },
  {
    group: 'Compliance',
    items: [
      { to: '/admin/compliance', label: 'Regras', icon: ShieldCheck },
      { to: '/admin/compliance?aba=eventos', label: 'Eventos', icon: ListChecks },
      { to: '/admin/compliance?aba=disjuntor', label: 'Ações em massa', icon: Scale, count: 'disjuntor' },
      { to: '/admin/compliance?aba=canais', label: 'Permissões por canal', icon: Plug },
      { to: '/admin/compliance?aba=regularizacao', label: 'Regularização pendente', icon: FileCheck2 },
      { to: '/admin/compliance?aba=retencao', label: 'Retenção de receitas', icon: History },
      { to: '/admin/compliance?aba=icms', label: 'ICMS e ALC', icon: Landmark },
    ],
  },
  { group: 'Infraestrutura', items: [{ to: '/admin/servidor', label: 'Servidor, filas e API', icon: Monitor }] },
];

/** Contadores das filas do Painel (só equipe; a cada minuto). */
function useAdminCounts(enabled: boolean) {
  return useQuery({ queryKey: ['admin', 'nav-counts'], queryFn: () => api<Record<string, number>>('/admin/nav-counts'), enabled, refetchInterval: 60_000, staleTime: 30_000 }).data ?? {};
}

/** Canais com conta conectada (ativa ou precisando reconectar). */
export function useConnectedChannels() {
  const q = useQuery({ queryKey: ['channels'], queryFn: () => api<ChannelInfo[]>('/channels'), staleTime: 60_000 });
  return (q.data ?? []).filter((c) => c.accounts.some((a) => a.status !== 'revoked'));
}

function NavList({ nav, collapsed, onNavigate, counts = {} }: { nav: NavSection[]; collapsed: boolean; onNavigate?: () => void; counts?: Record<string, number> }) {
  const loc = useLocation();
  // Telas com abas na navegação (ex.: /admin/compliance?aba=eventos): ativo pelo caminho E pela aba.
  const tabbed = new Set(nav.flatMap((sec) => sec.items).filter((i) => i.to?.includes('?')).map((i) => i.to!.split('?')[0]));
  const activeOf = (to: string) => {
    const [path, qs] = to.split('?');
    if (!tabbed.has(path!)) return undefined;
    return loc.pathname === path && (new URLSearchParams(qs ?? '').get('aba') ?? '') === (new URLSearchParams(loc.search).get('aba') ?? '');
  };
  return (
    <>
      {nav.map((section, i) => (
        <div key={i} style={{ display: 'contents' }}>
          {section.group && (
            <>
              <div className="group">{section.group}{(() => { const t = section.items.reduce((a, it) => a + (it.count ? counts[it.count] ?? 0 : 0), 0); return t ? ` (${t})` : ''; })()}</div>
              <div className="group-sep" />
            </>
          )}
          {section.items.map((item) => {
            const content = (
              <>
                <item.icon size={18} />
                <span className="label">{item.label}</span>
                {item.soon && <span className="soon-tag">em breve</span>}
                {item.count && !!counts[item.count] && <span className="nav-count" aria-label={`${counts[item.count]} na fila`}>{counts[item.count]}</span>}
              </>
            );
            const el = item.soon ? (
              <span className="nav soon" aria-disabled="true">{content}</span>
            ) : (
              activeOf(item.to!) !== undefined ? (
                // Tela com abas (ex.: /admin/compliance?aba=eventos): Link comum, ativo pelo caminho e pela aba. (A classe
                // é texto: o Tooltip junta o className das props, então não dá para usar a função do NavLink.)
                <Link to={item.to!} onClick={onNavigate} className={`nav${activeOf(item.to!) ? ' active' : ''}`} aria-current={activeOf(item.to!) ? 'page' : undefined}>
                  {content}
                </Link>
              ) : (
                <NavLink to={item.to!} end={item.end} className="nav" onClick={onNavigate}>
                  {content}
                </NavLink>
              )
            );
            return collapsed ? (
              <Tooltip.Root key={item.label}>
                <Tooltip.Trigger asChild>{el}</Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="right" sideOffset={8} className="tooltip">
                    {item.label}{item.soon ? ' (em breve)' : ''}{item.count && counts[item.count] ? ` (${counts[item.count]})` : ''}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            ) : (
              <span key={item.label} style={{ display: 'contents' }}>{el}</span>
            );
          })}
        </div>
      ))}
    </>
  );
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');

function UserMenu() {
  const me = useMe();
  const { prefs, update } = usePreferences();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    qc.clear();
    navigate('/login');
  };
  const switchTenant = async (tenantId: string) => {
    await api('/auth/tenant', { method: 'POST', json: { tenantId } });
    qc.clear();
    navigate('/');
  };

  const themes: [ThemePref, string, Icon][] = [
    ['system', 'Seguir o sistema', Monitor],
    ['light', 'Claro', Sun],
    ['dark', 'Escuro', Moon],
  ];
  const densities: [Density, string, Icon][] = [
    ['comfortable', 'Confortável', Rows2],
    ['compact', 'Compacta', Rows4],
  ];

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button className="user-btn" type="button" aria-label="Conta e aparência">
          <span className="avatar">{initials(me.user.name)}</span>
          <span className="who">
            <span>{me.user.name}</span>
            <small>{me.tenant.name}</small>
          </span>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content className="menu" align="end" sideOffset={8}>
          <div className="menu-label">Tema</div>
          {themes.map(([value, label, I]) => (
            <Dropdown.Item key={value} className="menu-item" onSelect={(e) => { e.preventDefault(); update({ theme: value }); }}>
              <I size={16} /> {label}
              {prefs.theme === value && <span className="ind"><Check size={16} /></span>}
            </Dropdown.Item>
          ))}
          <div className="menu-sep" />
          <div className="menu-label">Densidade das tabelas</div>
          {densities.map(([value, label, I]) => (
            <Dropdown.Item key={value} className="menu-item" onSelect={(e) => { e.preventDefault(); update({ density: value }); }}>
              <I size={16} /> {label}
              {prefs.density === value && <span className="ind"><Check size={16} /></span>}
            </Dropdown.Item>
          ))}
          {me.tenants.length > 1 && (
            <>
              <div className="menu-sep" />
              <div className="menu-label">Cliente</div>
              {me.tenants.map((t) => (
                <Dropdown.Item key={t.id} className="menu-item" onSelect={() => t.id !== me.tenant.id && switchTenant(t.id)}>
                  <Building2 size={16} /> {t.name}
                  {t.id === me.tenant.id && <span className="ind"><Check size={16} /></span>}
                </Dropdown.Item>
              ))}
            </>
          )}
          <div className="menu-sep" />
          <Dropdown.Item className="menu-item" onSelect={logout}>
            <LogOut size={16} /> Sair
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

export function Shell() {
  const qc = useQueryClient();
  useApplyPreferences();
  const { prefs, update } = usePreferences();
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();
  const narrow = useNarrowScreen();
  // Se o usuário nunca escolheu, o menu começa recolhido em notebooks e aberto em monitores grandes.
  const collapsed = prefs.sidebarCollapsed ?? narrow;
  const connected = useConnectedChannels();
  const channel = location.pathname.startsWith('/canal/mercadolivre') ? 'mercadolivre' : location.pathname.startsWith('/canal/shopee') ? 'shopee'
    : location.pathname.startsWith('/canal/ifood') ? 'ifood' : null;
  const selectedId = useSelectedAccountId(channel);
  const channelAccounts = connected.find((c) => c.code === channel)?.accounts.filter((a) => a.status !== 'revoked') ?? [];
  const currentAccount = channelAccounts.length > 1 ? pickAccount(channelAccounts, selectedId) : null;
  const me = useMe();
  const access = me.tenant.access;
  const navOf = (code: string | null) => navFor(code === 'shopee' ? SHOPEE_NAV : code === 'ifood' ? IFOOD_NAV : code === 'mercadolivre' ? MELI_NAV : NAV, access, me.tenant.vertical);
  /** Primeira tela do canal que o perfil pode abrir (a aba leva para ela). null: nenhuma, a aba some. */
  const channelHome = (code: string) => navOf(code).flatMap((s) => s.items).find((i) => i.to && !i.soon)?.to ?? null;
  const baseNav = navOf(channel === 'shopee' || channel === 'ifood' ? channel : 'mercadolivre');
  // Com várias lojas no canal, "Pedidos" abre só os da loja da aba.
  const channelNav = currentAccount
    ? baseNav.map((sec) => ({ ...sec, items: sec.items.map((i) => (i.to?.startsWith('/pedidos?channel=') ? { ...i, to: `/pedidos?account=${currentAccount.id}` } : i)) }))
    : baseNav;
  const inAdmin = location.pathname.startsWith('/admin') && !!me.user.platformRole;
  const home = homeOf(access);
  const generalNav = navOf(null);
  const nav = inAdmin ? ADMIN_NAV : channel ? channelNav : generalNav;
  const adminCounts = useAdminCounts(!!me.user.platformRole && !me.impersonating);
  // Tela que o perfil não acessa: a inicial vai para a primeira permitida; as outras mostram o aviso.
  const allowed = inAdmin || can(access, permOfPath(location.pathname));
  // Teste vencido: só a tela de aviso e os dados da empresa.
  const blocked = !!me.trial?.expired && location.pathname !== '/empresa' && !inAdmin;
  // No celular as abas somem: o menu lateral leva também aos canais conectados.
  const channelLinks = connected.flatMap((c) => { const to = channelHome(c.code); return to ? [{ to, label: c.name, icon: Megaphone }] : []; });
  const drawerNav: NavSection[] = [
    ...generalNav,
    ...(channelLinks.length ? [{ group: 'Canais', items: channelLinks }] : []),
  ];

  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="app" data-channel={channel ?? undefined}>
        {/* Painel Berytus: cabeçalho com cor própria e o selo ADMIN BERYTUS fixo, para nunca confundir com a área do cliente. */}
        <header className="band" data-admin={inAdmin ? '' : undefined}>
          <Dialog.Root open={drawer} onOpenChange={setDrawer}>
            <Dialog.Trigger asChild>
              <button className="icon-btn menu-btn" type="button" aria-label="Abrir menu"><Menu size={20} /></button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="drawer-overlay" />
              <Dialog.Content className="drawer side" aria-describedby={undefined}>
                <Dialog.Title className="sr-only">Menu</Dialog.Title>
                <NavList nav={[...(inAdmin ? ADMIN_NAV : channel ? channelNav : []), ...drawerNav, ...(me.user.platformRole && !inAdmin ? ADMIN_NAV : [])]} collapsed={false} onNavigate={() => setDrawer(false)} counts={adminCounts} />
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          <NavLink to={home} className="wordmark" aria-label="Berytus Conecta, visão geral"><BandLogo /></NavLink>
          {inAdmin && <span className="admin-badge" role="status" aria-label="Você está no Painel Berytus (admin)">ADMIN BERYTUS</span>}

          <nav className="tabs" aria-label="Canais">
            <Link to={home} className="tab" aria-current={channel || inAdmin ? undefined : 'page'}>
              Geral
            </Link>
            {connected.flatMap((c) => {
              const to = channelHome(c.code);
              if (!to) return [];
              // Duas lojas ou mais no mesmo canal: uma aba por loja (cada uma com anúncios, pedidos, preço e estoque próprios).
              const live = c.accounts.filter((a) => a.status !== 'revoked');
              const tabs = live.length > 1 ? live.map((a) => ({ key: a.id, account: a, label: a.displayName ?? a.externalAccountId })) : [{ key: c.code, account: null, label: c.name }];
              return tabs.map((t) => (
                <Link
                  key={t.key}
                  to={to}
                  onClick={() => { if (t.account && t.account.id !== currentAccount?.id) { selectAccount(c.code, t.account.id); qc.invalidateQueries(); } }}
                  className="tab"
                  data-channel={c.code}
                  title={t.account ? `${c.name} · ${t.label}` : undefined}
                  style={{ ['--swatch' as string]: `var(--ch-${c.code})` }}
                  aria-current={channel === c.code && (!t.account || t.account.id === currentAccount?.id) ? 'page' : undefined}
                >
                  <span className="swatch fill" /> {t.account ? <><span className="muted-tab">{c.name} ·</span> <span className="tab-name">{t.label}</span></> : c.name}
                  {(t.account ? t.account.status !== 'active' : c.accounts.some((a) => a.status !== 'active')) && <span className="tag warn" style={{ marginLeft: 2 }}>reconectar</span>}
                </Link>
              ));
            })}
            {connected.length === 0 && can(access, 'channel_accounts', 'write') && <Link to="/canais" className="tab">+ Conectar canal</Link>}
            {me.user.platformRole && (
              <Link to="/admin" className="tab" aria-current={inAdmin ? 'page' : undefined}><ShieldCheck size={15} /> Berytus</Link>
            )}
          </nav>

          <div className="band-right">
            <NotificationBell />
            <UserMenu />
          </div>
        </header>

        <div className={`body${collapsed ? ' collapsed' : ''}`}>
          <aside className="side" aria-label="Navegação">
            <NavList nav={nav} collapsed={collapsed} counts={adminCounts} />
            <div className="spacer" />
            <button
              type="button"
              className="nav collapse-btn"
              onClick={() => update({ sidebarCollapsed: !collapsed })}
              style={{ border: 0, background: 'none', cursor: 'pointer', width: '100%' }}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              <span className="label">Recolher menu</span>
            </button>
          </aside>
          <main className="main">
            <ImpersonationBar />
            {blocked ? <TrialEnded />
              : !allowed ? (location.pathname === '/' ? <Navigate to={home} replace /> : <NoAccess home={home} />)
              : <>{!inAdmin && <AccountNotices />}<Outlet /></>}
          </main>
        </div>
      </div>
    </Tooltip.Provider>
  );
}

function NoAccess({ home }: { home: string }) {
  return (
    <div className="panel" style={{ maxWidth: 560, margin: '40px auto', padding: 24, display: 'grid', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 20 }}>Sem acesso a esta tela</h2>
      <p className="muted" style={{ margin: 0 }}>O seu perfil de acesso não inclui esta tela. Se precisar dela, fale com o responsável pela conta.</p>
      <div><Link to={home} className="btn">Ir para o início</Link></div>
    </div>
  );
}
